import CRMNavigation from "@/components/CRMNavigation";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import {
  Users,
  TrendingUp,
  Workflow,
  CalendarDays,
  FileText,
  Keyboard,
  Box,
  HeartHandshake
} from "lucide-react";

export default function CRMSoftwarePage() {

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen">
      <CRMNavigation />

      {/* ==================== HERO ==================== */}
      <section
        className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#040814] text-white py-24"
        style={{ fontFamily: "'Roboto', sans-serif" }}
      >
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-[450px] h-[450px] bg-blue-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-[350px] h-[350px] bg-indigo-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* LEFT */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.6 }}
              className="text-white"
            >
              <h1 className="text-5xl font-black">Defining CRM</h1>

              <p className="mt-5 max-w-2xl text-white/90">
                A good CRM gives you insights into running your business. A smart CRM gives you the information you need in a way that you can use it. We also offering Free CRM software For Business so that you can understand its working, click on the link given below and start using it for free.
              </p>

              <div className="mt-8 flex gap-3">
                <a
                  href="/getintouch"
                  className="rounded-xl bg-white px-5 py-3 font-semibold text-[#1a4fb8] hover:bg-[#eef6ff] transition"
                >
                  Get Free CRM
                </a>

                <a
                  href="#strategy"
                  className="rounded-xl ring-1 ring-white/40 px-5 py-3 font-semibold text-white hover:bg-white/10 transition"
                >
                  Learn More
                </a>
              </div>
            </motion.div>

            {/* RIGHT */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative"
            >
              <img
                src="/crm-software.png"
                alt="CRM Illustration"
                className="w-full max-w-[560px] mx-auto rounded-3xl shadow-[0_12px_32px_rgba(2,6,23,.10)] ring-1 ring-white/10 bg-white/5 backdrop-blur"
              />
            </motion.div>

          </div>
        </div>
      </section>

      {/* ==================== STRATEGY ==================== */}
      <section id="strategy" className="py-16 lg:py-24 bg-white text-slate-900">
        <div className="container mx-auto px-6 grid gap-8 lg:grid-cols-2">
          {[1, 2].map((_, i) => (
            <motion.article
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="rounded-[20px] border border-slate-200 bg-white p-7 shadow-[0_12px_32px_rgba(2,6,23,.10)] hover:-translate-y-1 hover:shadow-2xl transition"
            >
              {i === 0 && (
                <>
                  <div className="mb-3 inline-flex text-xs font-bold uppercase text-[#184fb8]">
                    01 • Strategy
                  </div>
                  <h2 className="text-2xl font-extrabold text-slate-900">
                    CRM — The Strategy
                  </h2>
                  <p className="mt-4 text-slate-600 leading-7">
                    A good CRM gives you insights into running your business. A smart CRM gives you the information you need in a way that you can use it. How organizations approach client connections and building associations with them shapes their CRM technique. It includes gathering client information and breaking down the historical backdrop of client interactions to provide better services and build better relationships with clients, ultimately driving sales and revenue.
                  </p>
                </>
              )}

              {i === 1 && (
                <>
                  <div className="mb-3 inline-flex text-xs font-bold uppercase text-[#184fb8]">
                    02 • Product
                  </div>
                  <h2 className="text-2xl font-extrabold text-slate-900">
                    CRM Software — All You Need To Know
                  </h2>
                  <p className="mt-4 text-slate-600 leading-7">
                    A CRM system enables your business to offer a unique and consistent experience for your clients, providing a complete view of all client interactions, tracking sales, organizing opportunities, and promoting collaboration between teams.
                  </p>
                </>
              )}
            </motion.article>
          ))}
        </div>
      </section>

      {/* ==================== CUSTOMER RELATIONS ==================== */}
      <section className="py-16 lg:py-24 bg-[#040814] text-white">
        <div className="container mx-auto px-6 grid gap-10 lg:grid-cols-2">

          <motion.article
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl font-extrabold">
              Building great customer relations
            </h3>

            <div className="mt-5 space-y-4 leading-7 text-gray-300">
              <p>
                Incredible client connections depend on the predictable and customized experience offered by your business, paying little respect to where a client is in your business cycle. This implies each group in your association, from Marketing to Sales to Support must offer a steady encounter when connecting with a client.
              </p>

              <p>
                At the point when your business is beginning off and you're dealing with all your client data on spreadsheets/excel, this might be practical, however not prudent. The measure of time spent on data entry could be better spent acquiring clients and finalizing negotiations. This turns out to be exponentially increasingly entangled when you develop your business and grow your client base. This is the place CRM software becomes an integral factor. CRM software can be used to form a great relationship with your clients/customers.
              </p>
            </div>
          </motion.article>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <img
              src="/customer-relations.png"
              alt="Customer relations"
              className="w-full rounded-2xl border border-slate-200 shadow-[0_12px_32px_rgba(2,6,23,.10)] bg-white"
            />
          </motion.div>

        </div>
      </section>

      {/* ==================== BENEFITS ==================== */}
      <section id="benefits" className="py-20 lg:py-24 bg-[#f9fafb] text-slate-900">
        <div className="container mx-auto px-6 text-center max-w-5xl">
          <h3 className="text-3xl md:text-4xl font-extrabold text-slate-900">
            Benefits of CRM software
          </h3>

          <p className="mt-4 text-slate-600 leading-7">
            CRMs help you improve retention, workflows, data organization, and customer insights — all in one place.
          </p>
        </div>

        <div className="container mx-auto px-6 mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">

          {[{
            title: "Leads / Contacts / Companies / Deals",
            icon: Users,
            description: "Here Our CRM Work As Lead Management System Where You Can Manage All Your Leads, Lead Follow Up, Lead Tracker, Lead Generation, Your Company Contacts, Your Clients Company Details Etc."
          }, {
            title: "Boost sales",
            icon: TrendingUp,
            description: "A CRM can help you identify, nurture, and follow up on quality leads so you focus more on the customers most likely to convert."
          }, {
            title: "Efficient business process",
            icon: Workflow,
            description: "A CRM helps ensure processes are followed, improves productivity, and keeps teams aligned across every stage."
          }, {
            title: "Calendars & Scheduling",
            icon: CalendarDays,
            description: "Manage appointments and sync calendars (Google, Outlook, iPhone, Android) directly inside the CRM."
          }, {
            title: "Quotes & invoices",
            icon: FileText,
            description: "Create customizable quotes and invoices with your logo, custom fields, and layouts the way you prefer."
          }, {
            title: "Lesser data entry",
            icon: Keyboard,
            description: "Automate repetitive work like capturing leads and sending welcome emails, reducing manual spreadsheet entry."
          }, {
            title: "Product catalog",
            icon: Box,
            description: "Organize products, price lists, discounts, and track everything across the entire sales cycle."
          }, {
            title: "Enhanced customer relationships",
            icon: HeartHandshake,
            description: "Every interaction is stored in the CRM so you better understand customer needs and build stronger relationships."
          }].map((item, i) => {
            const Icon = item.icon;

            return (
              <motion.article
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className="rounded-2xl bg-white border border-slate-200 p-6 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>

                  <h4 className="text-lg font-semibold text-slate-900">
                    {item.title}
                  </h4>
                </div>

                <p className="mt-3 text-slate-600 text-sm leading-6">
                  {item.description}
                </p>
              </motion.article>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}